import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { extractSingleWord, extractContextSentence } from '../../lib/extractContextSentence';

/**
 * WordHighlightMenu wraps a reading surface and intercepts text selection.
 *
 * Props:
 *   userRole:          'teacher' | 'student'
 *   courseListOptions: array of { id, name, course_id, course_title } (optional)
 *   sourceType:        'Stimulus' | 'BookChapter' (for provenance tracking)
 *   sourceId:          the id of the Stimulus or BookChapter being read
 *   children:          the reading surface content to wrap
 */
export default function WordHighlightMenu({
  userRole,
  courseListOptions = null,
  sourceType = null,
  sourceId = null,
  children,
}) {
  const [menuState, setMenuState] = useState(null);
  const [fetchedLists, setFetchedLists] = useState(null);
  // Persistent toast for the add-to-list result. The small in-menu
  // "Added" text was hard to see for teachers especially — the menu
  // dismisses ~1.5s after success, so a quick blink at the selection
  // point is the only feedback. The toast renders top-center, shows
  // the word + list name, and persists ~3.5s independent of the menu.
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const containerRef = useRef(null);

  const closeMenu = useCallback(() => setMenuState(null), []);

  const showToast = useCallback((next) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(next);
    if (next) {
      toastTimerRef.current = setTimeout(() => setToast(null), 3500);
    }
  }, []);

  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  // Listen for mouseup on the container
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseUp = (e) => {
      // Don't intercept clicks on interactive elements
      if (e.target.closest('a, button, input, select, textarea, [role="button"]')) return;

      // Small delay to let selection settle
      setTimeout(() => {
        const selection = window.getSelection();
        const word = extractSingleWord(selection);

        if (!word) return;

        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const contextSentence = extractContextSentence(selection);

        setMenuState({
          x: rect.left + rect.width / 2,
          y: rect.top - 8,
          word,
          contextSentence,
          status: 'idle',
        });
      }, 50);
    };

    container.addEventListener('mouseup', handleMouseUp);
    return () => container.removeEventListener('mouseup', handleMouseUp);
  }, []);

  // Close on outside click or Escape. NOT on scroll: in the live-session
  // observer the activity mirror re-renders + nudges scroll as the student
  // types, which was dismissing the menu the instant before the teacher
  // could click "Add to list". The menu stays anchored to the selection;
  // the teacher dismisses it by clicking away or pressing Escape.
  useEffect(() => {
    if (!menuState) return;

    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-vocabulary-menu]')) closeMenu();
    };
    const handleKeyDown = (e) => { if (e.key === 'Escape') closeMenu(); };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuState, closeMenu]);

  const fetchTeacherLists = useCallback(async () => {
    if (fetchedLists) return fetchedLists;
    try {
      const res = await fetch('/teacher/vocabulary_lists_for_picker');
      const data = await res.json();
      setFetchedLists(data.lists);
      return data.lists;
    } catch {
      return [];
    }
  }, [fetchedLists]);

  const handleAddToPersonalList = async () => {
    if (!menuState) return;
    const word = menuState.word;
    setMenuState(s => ({ ...s, status: 'loading' }));

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch('/student/vocabulary/words', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({
          surface_form: word,
          context_sentence: menuState.contextSentence,
          source_reference_type: sourceType,
          source_reference_id: sourceId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMenuState(s => ({ ...s, status: 'success' }));
        showToast({ kind: 'success', word, label: 'your list' });
        setTimeout(closeMenu, 1200);
      } else {
        setMenuState(s => ({ ...s, status: 'error', errorMsg: data.error }));
        showToast({ kind: 'error', word, errorMsg: data.error || 'Could not add.' });
      }
    } catch {
      setMenuState(s => ({ ...s, status: 'error', errorMsg: 'Network error' }));
      showToast({ kind: 'error', word, errorMsg: 'Network error' });
    }
  };

  const handleAddToCourseList = async (listId) => {
    if (!menuState) return;
    const word = menuState.word;
    setMenuState(s => ({ ...s, status: 'loading' }));

    try {
      const allLists = courseListOptions || fetchedLists || [];
      const targetList = allLists.find(l => l.id === listId);
      if (!targetList) {
        setMenuState(s => ({ ...s, status: 'error', errorMsg: 'List not found' }));
        showToast({ kind: 'error', word, errorMsg: 'List not found' });
        return;
      }

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      const res = await fetch(
        `/teacher/courses/${targetList.course_id}/vocabulary/lists/${listId}/words`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
          body: JSON.stringify({
            surface_form: word,
            context_sentence: menuState.contextSentence,
            source_reference_type: sourceType,
            source_reference_id: sourceId,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setMenuState(s => ({ ...s, status: 'success' }));
        showToast({ kind: 'success', word, label: targetList.name });
        setTimeout(closeMenu, 1200);
      } else {
        setMenuState(s => ({ ...s, status: 'error', errorMsg: data.error }));
        showToast({ kind: 'error', word, errorMsg: data.error || 'Could not add.' });
      }
    } catch {
      setMenuState(s => ({ ...s, status: 'error', errorMsg: 'Network error' }));
      showToast({ kind: 'error', word, errorMsg: 'Network error' });
    }
  };

  const handleOpenPicker = async () => {
    setMenuState(s => ({ ...s, status: 'picking' }));
    if (!courseListOptions) {
      await fetchTeacherLists();
    }
  };

  const lists = courseListOptions || fetchedLists || [];

  return (
    <div ref={containerRef} className="relative">
      {children}

      {menuState && (
        <FloatingMenu
          menuState={menuState}
          userRole={userRole}
          lists={lists}
          onAddToPersonal={handleAddToPersonalList}
          onAddToCourse={handleAddToCourseList}
          onOpenPicker={handleOpenPicker}
          onClose={closeMenu}
        />
      )}

      {toast && <AddToListToast toast={toast} />}
    </div>
  );
}

function AddToListToast({ toast }) {
  const isSuccess = toast.kind === 'success';
  return (
    <div
      role="status"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 pointer-events-none animate-in fade-in slide-in-from-top-2 duration-200"
    >
      <div
        className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg text-sm font-medium border ${
          isSuccess
            ? 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-100 dark:border-emerald-700'
            : 'bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-900/40 dark:text-rose-100 dark:border-rose-700'
        }`}
      >
        {isSuccess ? (
          <>
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-7 7a1 1 0 01-1.4 0l-3-3a1 1 0 011.4-1.4L9 11.6l6.3-6.3a1 1 0 011.4 0z" clipRule="evenodd" />
            </svg>
            <span>
              Added <span className="font-semibold">&ldquo;{toast.word}&rdquo;</span> to{' '}
              <span className="font-semibold">{toast.label}</span>
            </span>
          </>
        ) : (
          <>
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M10 1a9 9 0 100 18 9 9 0 000-18zm-.75 5.25a.75.75 0 011.5 0v4a.75.75 0 01-1.5 0v-4zm.75 8.25a1 1 0 110-2 1 1 0 010 2z" clipRule="evenodd" />
            </svg>
            <span>
              Couldn't add <span className="font-semibold">&ldquo;{toast.word}&rdquo;</span>
              {toast.errorMsg ? ` — ${toast.errorMsg}` : ''}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function FloatingMenu({ menuState, userRole, lists, onAddToPersonal, onAddToCourse, onOpenPicker }) {
  const { t } = useTranslation();
  const { x, y, word, status, errorMsg } = menuState;

  return (
    <div
      data-vocabulary-menu
      className="fixed z-50 pointer-events-auto"
      style={{
        left: `${x}px`,
        top: `${y}px`,
        transform: 'translate(-50%, -100%)',
      }}
    >
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg px-3 py-2 text-sm min-w-[160px]">
        {/* Word label */}
        <div className="font-semibold text-gray-900 dark:text-white mb-1.5 text-center">
          &ldquo;{word}&rdquo;
        </div>

        {status === 'success' && (
          <p className="text-green-600 dark:text-green-400 text-center text-xs py-1">Added</p>
        )}
        {status === 'error' && (
          <p className="text-red-500 text-center text-xs py-1">{errorMsg || 'Error'}</p>
        )}
        {status === 'loading' && (
          <p className="text-gray-400 text-center text-xs py-1">Adding...</p>
        )}

        {(status === 'idle' || status === 'picking') && userRole === 'student' && (
          <button
            onClick={onAddToPersonal}
            className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 transition-colors"
          >
            {t('wordHighlight.addToMyList')}
          </button>
        )}

        {status === 'idle' && userRole === 'teacher' && lists.length === 1 && (
          <button
            onClick={() => onAddToCourse(lists[0].id)}
            className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 transition-colors"
          >
            {t('wordHighlight.addToList', { name: lists[0].name })}
          </button>
        )}

        {status === 'idle' && userRole === 'teacher' && lists.length !== 1 && (
          <button
            onClick={onOpenPicker}
            className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 transition-colors"
          >
            {t('wordHighlight.addToCourseList')}
          </button>
        )}

        {status === 'picking' && userRole === 'teacher' && (
          <div className="mt-1">
            {lists.length === 0 ? (
              <p className="text-xs text-gray-400 px-2 py-1">{t('wordHighlight.noCourseListsYet')}</p>
            ) : (
              <div className="max-h-40 overflow-y-auto space-y-0.5">
                {lists.map(list => (
                  <button
                    key={list.id}
                    onClick={() => onAddToCourse(list.id)}
                    className="w-full text-left px-2 py-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <span className="text-gray-700 dark:text-gray-300 text-xs block">{list.name}</span>
                    {list.course_title && (
                      <span className="text-gray-400 dark:text-gray-500 text-xs">{list.course_title}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Caret */}
      <div
        className="w-0 h-0 mx-auto border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white dark:border-t-gray-900"
        style={{ filter: 'drop-shadow(0 1px 0 #e5e7eb)' }}
      />
    </div>
  );
}
